"use client";

import { Box, Stack, Table, TableBody, TableCell, TableRow, Theme, Typography } from "@mui/material";
import { JSX } from "react/jsx-runtime";

type JsonObject = { [key: string]: JsonValue };
type JsonValue = string | number | boolean | null | JsonObject | JsonValue[];

type JsonViewerProps = {
  data: JsonObject;
};

const READABLE_JSON_KEYS: Record<string, string> = {
  new_dirty_reason: "Details",
  new_dirty_line: "Dirty line",
  fromPath: "From path",
  message: "Message",
  to: "To",
  toPath: "To path",
  value: "Value",
};

const border = (theme: Theme) => `1px solid ${theme.palette.surfaceLight30.main}`;

const EmptyTypography = ({ value }: { value: string }) => (
  <Stack sx={{ padding: "12px 8px", width: "100%" }}>
    <Typography
      sx={{
        color: "text30.main",
        fontStyle: "italic",
        display: "inline-block",
        marginRight: "auto",
      }}
    >
      {value}
    </Typography>
  </Stack>
);

const PrimitiveValueViewer = ({ value }: { value: string | boolean | number | null }) => (
  <Stack sx={{ padding: "12px 8px", width: "100%" }}>
    <Typography
      component="span"
      sx={{
        marginRight: "auto",
        color: typeof value !== "boolean" && value ? "text50.main" : "text30.main",
        fontStyle: typeof value !== "boolean" && value ? undefined : "italic",
      }}
    >
      {typeof value === "boolean" || value === null ? String(value) : value || "Empty"}
    </Typography>
  </Stack>
);

const ArrayValueViewer = ({
  value,
  renderValue,
}: {
  value: JsonValue[];
  renderValue: (value: JsonValue) => JSX.Element;
}) => {
  if (!value.length) return <EmptyTypography value="[]" />;

  return (
    <Box sx={{ padding: 0 }}>
      {value.map((item, idx) => (
        <Stack key={idx} direction="row">
          <Stack
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderTop: border,
            }}
          >
            {value.length > 1 ? (
              <Typography variant="body.xSmall.regular" sx={{ padding: "8px" }}>
                {idx + 1}
              </Typography>
            ) : null}
          </Stack>
          <Box key={idx} sx={{ width: "100%" }}>
            {renderValue(item)}
          </Box>
        </Stack>
      ))}
    </Box>
  );
};

const KeyObjectViewer = ({
  value,
  renderValue,
}: {
  value: JsonObject;
  renderValue: (value: JsonValue) => JSX.Element;
}) => {
  const keys = Object.keys(value);

  if (!keys.length) return <EmptyTypography value="{}" />;

  return (
    <Table size="small" sx={{ pt: "8px", pb: "8px" }}>
      <TableBody>
        {keys.map((k) => (
          <TableRow
            key={k}
            sx={{
              borderTop: border,
              borderLeft: border,
              backgroundColor: "surfaceLight10.main",
            }}
          >
            <TableCell
              sx={{
                padding: "12px 24px",
                borderTop: border,
                borderRight: border,
                borderBottom: border,
              }}
            >
              <Typography
                variant="body.medium.bold"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {READABLE_JSON_KEYS[k] || k}
              </Typography>
            </TableCell>
            <TableCell sx={{ padding: 0, border: "none", width: "100%" }}>{renderValue(value[k])}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const JsonViewer = ({ data }: JsonViewerProps) => {
  const renderValue = (value: JsonValue): JSX.Element => {
    if (typeof value === "string" || typeof value === "boolean" || typeof value === "number" || value === null) {
      return <PrimitiveValueViewer value={value} />;
    }

    if (Array.isArray(value)) {
      return <ArrayValueViewer value={value} renderValue={renderValue} />;
    }

    return <KeyObjectViewer renderValue={renderValue} value={value} />;
  };

  return renderValue(data);
};

export { JsonViewer };
